import { useState } from 'react';

import { motion } from 'motion/react';

import { AIAgentBanner } from '@src/components/AIAgentBanner';
import { ScreenshotGallery } from '@src/components/ScreenshotGallery';
import { Button } from '@src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

// Screenshots
import {
  Activity,
  AlertTriangle,
  AppWindow,
  ArrowRight,
  AtSign,
  BarChart3,
  Bot,
  Building2,
  CheckCircle,
  Cloud,
  CloudCog,
  Cpu,
  Database,
  ExternalLink,
  Eye,
  FolderOpen,
  Gauge,
  GitBranch,
  GitFork,
  Globe,
  HardDrive,
  HardDriveDownload,
  Heart,
  History,
  KeyRound,
  Layers,
  Lock,
  MemoryStick,
  Monitor,
  Network,
  Package,
  PieChart,
  Printer,
  RefreshCw,
  Search,
  Server,
  Share2,
  Shield,
  Timer,
  Users,
  UsersRound,
  Wifi,
  XCircle,
  Zap,
} from 'lucide-react';

import cmdbHygieneIdentity from '@src/assets/screenshots/cmdb-hygiene-identity.webp';
import cmdbHygieneNetwork from '@src/assets/screenshots/cmdb-hygiene-network.webp';
import cmdbHygieneOverview from '@src/assets/screenshots/cmdb-hygiene-overview.webp';
import cmdbHygieneProcesses from '@src/assets/screenshots/cmdb-hygiene-processes.webp';
import cmdbHygieneServices from '@src/assets/screenshots/cmdb-hygiene-services.webp';
import cmdbHygieneSoftware from '@src/assets/screenshots/cmdb-hygiene-software.webp';
import cmdbHygieneSystem from '@src/assets/screenshots/cmdb-hygiene-system.webp';
import cmdbShadowIt from '@src/assets/screenshots/cmdb-shadow-it.webp';
import AnswerSection from '@src/components/seo/AnswerSection';

const BOSSS_BLUE = '#2973a3';
const BG_COLOR = '#F8FAFC';


// Pain points that customers face
const painPoints = [
  {
    icon: XCircle,
    problem: 'Shadow IT Blind Spots',
    description: 'Unauthorized cloud services, apps, and devices operating outside IT control create security vulnerabilities and compliance gaps.',
    solution: 'Multi-source discovery with automatic sync from Azure, Intune, Wazuh, and Entra ID reveals your complete asset landscape.',
  },
  {
    icon: Timer,
    problem: 'Manual Asset Tracking',
    description: 'Spreadsheets and manual processes can\'t keep pace with dynamic cloud environments and hybrid workforces.',
    solution: 'Real-time synchronization automatically updates your CMDB as assets are created, modified, or decommissioned.',
  },
  {
    icon: AlertTriangle,
    problem: 'Configuration Drift',
    description: 'Untracked changes lead to misconfigurations, security incidents, and failed audits without visibility into what changed.',
    solution: 'Comprehensive change history with audit trails for every asset modification across your entire infrastructure.',
  },
  {
    icon: Layers,
    problem: 'Siloed Asset Data',
    description: 'Different teams manage their own inventories in disconnected tools, creating inconsistent and duplicate records.',
    solution: 'Unified asset repository with 22 asset categories and comprehensive relationship mappings in a single source of truth.',
  },
];

// Asset categories from screenshots - 22 categories
const assetCategories = [
  { name: 'AI Models', icon: Bot, description: 'Track deployed AI/ML models, versions, and training data lineage' },
  { name: 'APIs', icon: Globe, description: 'Catalog internal and external API endpoints with usage analytics' },
  { name: 'App Registrations', icon: KeyRound, description: 'Azure AD and OAuth app registrations with permission scopes' },
  { name: 'Applications', icon: AppWindow, description: 'Business applications with ownership, licensing, and dependencies' },
  { name: 'Backups', icon: HardDriveDownload, description: 'Backup jobs, schedules, retention policies, and recovery points' },
  { name: 'Cloud', icon: CloudCog, description: 'Multi-cloud resources across Azure, AWS, and GCP environments' },
  { name: 'DNS', icon: Globe, description: 'DNS zones, records, and domain management across providers' },
  { name: 'Databases', icon: Database, description: 'Database instances, schemas, and replication configurations' },
  { name: 'Devices', icon: Monitor, description: 'Endpoints with hardware specs, agent status, and last sign-in tracking' },
  { name: 'Disks', icon: HardDrive, description: 'Storage volumes, partitions, and capacity utilization metrics' },
  { name: 'Distribution Lists', icon: UsersRound, description: 'Email distribution groups and membership management' },
  { name: 'Drones', icon: Wifi, description: 'IoT devices, sensors, and edge computing nodes' },
  { name: 'Logs', icon: Activity, description: 'Log sources, retention settings, and aggregation pipelines' },
  { name: 'Networks', icon: Network, description: 'VLANs, subnets, firewall rules, and network topology' },
  { name: 'Printers', icon: Printer, description: 'Print devices, queues, and driver management' },
  { name: 'Repositories', icon: GitFork, description: 'Code repositories with branch policies and access controls' },
  { name: 'SharePoints', icon: FolderOpen, description: 'SharePoint sites, libraries, and permission inheritance' },
  { name: 'Shares', icon: Share2, description: 'Network file shares with access permissions and quotas' },
  { name: 'Social Media', icon: AtSign, description: 'Corporate social accounts and posting authorization' },
  { name: 'Software', icon: Package, description: 'Comprehensive software inventory with vendor and version tracking' },
  { name: 'Teams', icon: Users, description: 'Microsoft Teams channels, apps, and guest access policies' },
  { name: 'Web Sites', icon: ExternalLink, description: 'Web properties, SSL certificates, and hosting configurations' },
];

// Inventory sources from screenshots
const inventorySources = [
  { name: 'Entra ID', color: '#0078d4', description: 'Microsoft Entra ID (Azure AD) identity sync' },
  { name: 'Intune', color: '#0078d4', description: 'Microsoft Intune device management' },
  { name: 'Azure VM', color: '#0078d4', description: 'Azure virtual machine inventory' },
  { name: 'Wazuh', color: '#00a86b', description: 'Wazuh security monitoring agents' },
  { name: 'Azure Cloud', color: '#0078d4', description: 'Azure resource discovery' },
];

// Dashboard metrics from screenshots
const dashboardMetrics = [
  { label: 'Total Systems', value: '34', icon: Server, trend: '+3 this week' },
  { label: 'Software Items', value: 'All', icon: Package, trend: 'Across all systems' },
  { label: 'Software Vendors', value: '318', icon: Building2, trend: 'Tracked vendors' },
  { label: 'Relationships', value: 'All', icon: GitBranch, trend: 'Asset connections' },
];

// OS distribution data from screenshots
const osDistribution = [
  { name: 'Ubuntu', count: 10, color: '#E95420' },
  { name: 'RHEL', count: 4, color: '#EE0000' },
  { name: 'Windows', count: 6, color: '#0078d4' },
  { name: 'iOS', count: 3, color: '#000000' },
  { name: 'Windows Server', count: 4, color: '#0078d4' },
  { name: 'Fedora', count: 1, color: '#294172' },
  { name: 'Kali', count: 1, color: '#557C94' },
];

export function CMDBPage() {
  const [activeTab, setActiveTab] = useState('all');
  const onNavigateToTop = useNavigateToTop();

  // Group categories for tab filtering
  const categoryGroups = {
    all: assetCategories,
    infrastructure: assetCategories.filter((c) => ['Devices', 'Cloud', 'Networks', 'Databases', 'Disks', 'DNS'].includes(c.name)),
    applications: assetCategories.filter((c) => ['Applications', 'APIs', 'Software', 'App Registrations', 'AI Models', 'Repositories'].includes(c.name)),
    collaboration: assetCategories.filter((c) => ['Teams', 'SharePoints', 'Distribution Lists', 'Shares', 'Social Media', 'Web Sites'].includes(c.name)),
    operations: assetCategories.filter((c) => ['Backups', 'Logs', 'Printers', 'Drones'].includes(c.name)),
  };

  const tabs = [
    { id: 'all', label: 'All Categories', count: 22 },
    { id: 'infrastructure', label: 'Infrastructure', count: 6 },
    { id: 'applications', label: 'Applications', count: 6 },
    { id: 'collaboration', label: 'Collaboration', count: 6 },
    { id: 'operations', label: 'Operations', count: 4 },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #dbeafe 50%, #bfdbfe 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-20 left-20"
            animate={{ rotate: [0, 10, -10, 0], y: [0, -15, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Database className="w-8 h-8" style={{ color: `${BOSSS_BLUE}50` }} />
          </motion.div>

          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            className="absolute bottom-40 right-20"
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Server className="w-6 h-6" style={{ color: `${BOSSS_BLUE}40` }} />
          </motion.div>

          <motion.div
            animate={{ y: [0, -20, 0] }}
            className="absolute top-40 right-1/4"
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Network className="w-5 h-5" style={{ color: `${BOSSS_BLUE}45` }} />
          </motion.div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div
                style={{ backgroundColor: `${BOSSS_BLUE}15`, color: BOSSS_BLUE }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <Database className="w-4 h-4 mr-2" />
                Configuration Management Database
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Know Every Asset. See Every Connection.
              </h1>

              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                BOSSS CMDB automatically discovers, catalogs, and tracks every asset in your
                environment—from cloud VMs to AI models—with real-time sync from Azure, Intune,
                Wazuh, and more.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {dashboardMetrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="text-center p-3 bg-white/80 rounded-lg shadow-sm"
                  >
                    <div style={{ color: BOSSS_BLUE }} className="text-2xl font-bold">{metric.value}</div>
                    <div className="text-xs text-gray-600">{metric.label}</div>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    style={{ backgroundColor: BOSSS_BLUE }}
                    className="text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl"
                    onClick={() => onNavigateToTop('/demo')}
                  >
                    Schedule a Demo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>

                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    variant="outline"
                    style={{ borderColor: BOSSS_BLUE, color: BOSSS_BLUE }}
                    className="px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg"
                    onClick={() => onNavigateToTop('/contact')}
                  >
                    Contact Us
                  </Button>
                </motion.div>
              </div>
            </motion.div>

            {/* IT Hygiene Dashboard Preview */}
            <motion.div
              className="relative"
              animate={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white rounded-xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div style={{ backgroundColor: `${BOSSS_BLUE}15` }} className="w-10 h-10 rounded-lg flex items-center justify-center">
                      <Gauge className="w-5 h-5" style={{ color: BOSSS_BLUE }} />
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900">IT Hygiene Dashboard</h3>
                      <p className="text-xs text-gray-500">Real-time infrastructure overview</p>
                    </div>
                  </div>

                  <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">Live</span>
                </div>

                {/* Mini OS Distribution */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 mb-3">Operating System Distribution</p>

                  <div className="space-y-2">
                    {osDistribution.slice(0, 4).map((os, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-gray-600 w-20">{os.name}</span>

                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <motion.div
                            initial={{ width: '0%' }}
                            className="h-2 rounded-full"
                            style={{ backgroundColor: os.color }}
                            animate={{ width: `${(os.count / 10) * 100}%` }}
                            transition={{ duration: 0.8, delay: 0.5 + i * 0.1 }}
                          />
                        </div>

                        <span className="text-xs font-medium text-gray-900 w-6">{os.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mini Stats Row */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { icon: Cpu, label: 'Online', value: '28', color: '#10b981' },
                    { icon: Activity, label: 'Offline', value: '6', color: '#ef4444' },
                    { icon: RefreshCw, label: 'Syncing', value: '2', color: '#f59e0b' },
                  ].map((stat, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-3 text-center">
                      <stat.icon style={{ color: stat.color }} className="w-4 h-4 mx-auto mb-1" />
                      <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Inventory Sources */}
                <div className="flex flex-wrap gap-2">
                  {inventorySources.slice(0, 4).map((source, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{ backgroundColor: `${source.color}15`, color: source.color }}
                    >
                      {source.name}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/platform/cmdb" />


      {/* Pain Points Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Stop Fighting Asset Management Chaos
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              IT teams waste countless hours hunting for asset information across disconnected tools.
              Here&apos;s how BOSSS CMDB brings order to the chaos.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-red-100">
                        <point.icon className="w-6 h-6 text-red-600" />
                      </div>

                      <div>
                        <CardTitle className="text-xl text-gray-900 mb-2">
                          {point.problem}
                        </CardTitle>

                        <p className="text-gray-600 text-sm">
                          {point.description}
                        </p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div style={{ backgroundColor: `${BOSSS_BLUE}08` }} className="flex items-start gap-4 p-4 rounded-lg">
                      <div style={{ backgroundColor: `${BOSSS_BLUE}20` }} className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4" style={{ color: BOSSS_BLUE }} />
                      </div>

                      <div>
                        <p style={{ color: BOSSS_BLUE }} className="text-sm font-medium">BOSSS Solution</p>
                        <p className="text-sm text-gray-700 mt-1">{point.solution}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Screenshot Section */}
      <section className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A unified dashboard that gives you complete visibility into your infrastructure health,
              software inventory, and system distribution at a glance.
            </p>
          </motion.div>

          <ScreenshotGallery
            accentColor={BOSSS_BLUE}
            urlPath="cmdb/dashboard"
            screenshots={[
              { src: cmdbHygieneOverview, label: 'Overview', alt: 'BOSSS IT Hygiene dashboard showing infrastructure health across all asset categories' },
              { src: cmdbHygieneSystem, label: 'System', alt: 'BOSSS CMDB system inventory with operating system family distribution' },
              { src: cmdbHygieneSoftware, label: 'Software', alt: 'BOSSS CMDB software inventory with package analytics and end-of-life exposure' },
              { src: cmdbHygieneNetwork, label: 'Network', alt: 'BOSSS CMDB network inventory covering DNS, firewall rules and network segments' },
              { src: cmdbHygieneIdentity, label: 'Identity', alt: 'BOSSS CMDB identity inventory showing every identity, its source system and privilege level' },
              { src: cmdbHygieneServices, label: 'Services', alt: 'BOSSS CMDB services view showing listening services and device distribution' },
              { src: cmdbHygieneProcesses, label: 'Processes', alt: 'BOSSS CMDB running process inventory across managed endpoints' },
              { src: cmdbShadowIt, label: 'Shadow IT', alt: 'BOSSS CMDB shadow IT discovery surfacing unsanctioned services from usage telemetry' },
            ]}
          />

          {/* Feature Callouts */}
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
          >
            {[
              { icon: Server, label: 'Real-time Device Tracking', value: '129 devices' },
              { icon: Package, label: 'Software Inventory', value: '21 packages' },
              { icon: Building2, label: 'Vendor Management', value: '9 vendors' },
              { icon: Activity, label: 'Process Monitoring', value: '20 processes' },
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-white rounded-xl shadow-md border border-gray-100">
                <item.icon style={{ color: BOSSS_BLUE }} className="w-6 h-6 mx-auto mb-2" />
                <p className="text-lg font-bold text-gray-900">{item.value}</p>
                <p className="text-xs text-gray-500">{item.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 22 Asset Categories Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              22 Asset Categories. One Unified View.
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From traditional infrastructure to modern AI models, BOSSS CMDB tracks every type of asset
              your organization relies on.
            </p>
          </motion.div>

          {/* Tab Navigation - Mobile Dropdown */}
          <div className="md:hidden mb-12">
            <select
              value={activeTab}
              style={{ color: BOSSS_BLUE }}
              className="w-full px-4 py-3 bg-white rounded-xl shadow-md border border-gray-200 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => setActiveTab(e.target.value)}
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tab.label}
                  {' '}
                  (
                  {tab.count}
                  )
                </option>
              ))}
            </select>
          </div>

          {/* Tab Navigation - Desktop Tabs */}
          <div className="hidden md:flex justify-center mb-12">
            <div className="inline-flex bg-white rounded-xl p-1 shadow-md border border-gray-200">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  style={{
                    backgroundColor: activeTab === tab.id ? BOSSS_BLUE : 'transparent',
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}

                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Categories Grid */}
          <motion.div
            key={activeTab}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            initial={{ opacity: 0, y: 20 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          >
            {categoryGroups[activeTab].map((category, index) => (
              <motion.div
                key={category.name}
                animate={{ opacity: 1, scale: 1 }}
                initial={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-lg hover:border-blue-200 transition-all duration-200 group">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div
                        style={{ backgroundColor: `${BOSSS_BLUE}12` }}
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                      >
                        <category.icon className="w-5 h-5" style={{ color: BOSSS_BLUE }} />
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1 group-hover:text-blue-700 transition-colors">
                          {category.name}
                        </h4>

                        <p className="text-xs text-gray-600 leading-relaxed">
                          {category.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Multi-Source Discovery Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Multi-Source Asset Discovery
              </h2>

              <p className="text-lg text-gray-600 mb-6">
                BOSSS CMDB automatically syncs with your existing tools to build a complete
                picture of your environment—no manual data entry required.
              </p>

              <div className="space-y-4">
                {inventorySources.map((source, i) => (
                  <motion.div
                    key={i}
                    viewport={{ once: true }}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:shadow-md transition-all"
                  >
                    <div
                      style={{ backgroundColor: `${source.color}15` }}
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                    >
                      <RefreshCw className="w-5 h-5" style={{ color: source.color }} />
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900">{source.name}</h4>
                      <p className="text-sm text-gray-600">{source.description}</p>
                    </div>

                    <div className="ml-auto">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                        Auto-sync
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Sync Benefits */}
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Why Multi-Source Matters</h3>

                <div className="space-y-6">
                  {[
                    {
                      icon: Eye,
                      title: 'Complete Visibility',
                      description: 'See every asset regardless of which tool manages it—no more blind spots.',
                    },
                    {
                      icon: RefreshCw,
                      title: 'Always Current',
                      description: 'Real-time sync ensures your CMDB reflects reality, not last month\'s snapshot.',
                    },
                    {
                      icon: Shield,
                      title: 'Security Context',
                      description: 'Wazuh integration adds security posture data to every asset record.',
                    },
                    {
                      icon: Zap,
                      title: 'Zero Manual Entry',
                      description: 'Eliminate data entry errors and free your team for strategic work.',
                    },
                  ].map((benefit, i) => (
                    <motion.div
                      key={i}
                      viewport={{ once: true }}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      className="flex items-start gap-4"
                      transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
                    >
                      <div
                        style={{ backgroundColor: `${BOSSS_BLUE}15` }}
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      >
                        <benefit.icon className="w-5 h-5" style={{ color: BOSSS_BLUE }} />
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900">{benefit.title}</h4>
                        <p className="text-sm text-gray-600">{benefit.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Sync Stats */}
                <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-gray-200">
                  {[
                    { value: '5+', label: 'Data Sources' },
                    { value: '< 5min', label: 'Sync Interval' },
                    { value: '100%', label: 'Automated' },
                  ].map((stat, i) => (
                    <div key={i} className="text-center">
                      <p style={{ color: BOSSS_BLUE }} className="text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* IT Hygiene Analytics Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              IT Hygiene Analytics at a Glance
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get instant visibility into your infrastructure health with dashboards that surface
              what matters most—from OS distribution to resource utilization.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* OS Distribution Card */}
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Operating System Families</CardTitle>
                    <PieChart className="w-5 h-5 text-gray-400" />
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="space-y-3">
                    {osDistribution.map((os, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: os.color }} />
                        <span className="text-sm text-gray-600 flex-1">{os.name}</span>

                        <div className="w-24 bg-gray-100 rounded-full h-2">
                          <motion.div
                            initial={{ width: '0%' }}
                            viewport={{ once: true }}
                            className="h-2 rounded-full"
                            style={{ backgroundColor: os.color }}
                            transition={{ duration: 0.8, delay: i * 0.1 }}
                            whileInView={{ width: `${(os.count / 10) * 100}%` }}
                          />
                        </div>

                        <span className="text-sm font-medium text-gray-900 w-6 text-right">{os.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Platform Distribution */}
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Platform Distribution</CardTitle>
                    <BarChart3 className="w-5 h-5 text-gray-400" />
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    {[
                      { name: 'Linux', value: 60, color: '#f97316' },
                      { name: 'Windows', value: 30, color: '#0078d4' },
                      { name: 'macOS', value: 10, color: '#000000' },
                    ].map((platform, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">{platform.name}</span>

                          <span className="font-medium text-gray-900">
                            {platform.value}
                            %
                          </span>
                        </div>

                        <div className="w-full bg-gray-100 rounded-full h-3">
                          <motion.div
                            initial={{ width: '0%' }}
                            viewport={{ once: true }}
                            className="h-3 rounded-full"
                            style={{ backgroundColor: platform.color }}
                            whileInView={{ width: `${platform.value}%` }}
                            transition={{ duration: 0.8, delay: 0.3 + i * 0.1 }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Architecture Distribution */}
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Architecture Distribution</CardTitle>
                    <Cpu className="w-5 h-5 text-gray-400" />
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="flex items-center justify-center py-4">
                    <div className="relative w-40 h-40">
                      {/* Donut chart visualization */}
                      <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                        <circle r="40" cx="50" cy="50" fill="none" stroke="#e5e7eb" strokeWidth="12" />

                        <motion.circle
                          r="40"
                          cx="50"
                          cy="50"
                          fill="none"
                          strokeWidth="12"
                          stroke={BOSSS_BLUE}
                          strokeDasharray="251.2"
                          viewport={{ once: true }}
                          initial={{ strokeDashoffset: 251.2 }}
                          transition={{ duration: 1, delay: 0.5 }}
                          whileInView={{ strokeDashoffset: 50.24 }}
                        />

                        <motion.circle
                          r="40"
                          cx="50"
                          cy="50"
                          fill="none"
                          stroke="#10b981"
                          strokeWidth="12"
                          strokeDasharray="251.2"
                          viewport={{ once: true }}
                          style={{ strokeDashoffset: 201 }}
                          initial={{ strokeDashoffset: 251.2 }}
                          transition={{ duration: 1, delay: 0.7 }}
                          whileInView={{ strokeDashoffset: 175.84 }}
                        />
                      </svg>

                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold text-gray-900">34</span>
                        <span className="text-xs text-gray-500">Systems</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BOSSS_BLUE }} />
                      <span className="text-sm text-gray-600">x86_64 (80%)</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <span className="text-sm text-gray-600">arm64 (20%)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Additional Metrics Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              { label: 'Top Software Vendor', value: 'Ubuntu Desktop', icon: Package },
              { label: 'Avg Memory Usage', value: '12.5 GB', icon: MemoryStick },
              { label: 'Shadow IT Detected', value: '0', icon: Eye },
              { label: 'Third-Party Managed', value: '0', icon: Shield },
            ].map((stat, i) => (
              <motion.div
                key={i}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
              >
                <Card className="text-center p-4">
                  <stat.icon style={{ color: BOSSS_BLUE }} className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Built for Modern IT Operations
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every feature designed to give you complete control over your infrastructure.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: RefreshCw,
                title: 'Real-Time Sync',
                description: 'Automatic synchronization with Azure, Intune, Wazuh, and Entra ID keeps your CMDB always current.',
              },
              {
                icon: GitBranch,
                title: 'Relationship Mapping',
                description: 'Comprehensive relationship mappings show how assets connect for impact analysis and root cause identification.',
              },
              {
                icon: History,
                title: 'Change History',
                description: 'Complete audit trail of every configuration change with who, what, when, and why.',
              },
              {
                icon: Search,
                title: 'Powerful Search',
                description: 'Find any asset instantly with advanced filtering across 22 asset categories.',
              },
              {
                icon: Activity,
                title: 'Agent Monitoring',
                description: 'Track agent status (Online/Offline) and last sign-in dates for all managed devices.',
              },
              {
                icon: Cloud,
                title: 'Cloud XDR Integration',
                description: 'Direct integration with Cloud XDR for extended detection and response capabilities.',
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 group">
                  <CardHeader className="pb-4">
                    <div
                      style={{ backgroundColor: `${BOSSS_BLUE}15` }}
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    >
                      <feature.icon className="w-6 h-6" style={{ color: BOSSS_BLUE }} />
                    </div>

                    <CardTitle className="text-xl group-hover:text-blue-600 transition-colors">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Use Cases */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Trusted Across Industries
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              BOSSS CMDB adapts to your industry&apos;s specific asset management and compliance requirements.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Building2,
                title: 'Financial Services',
                frameworks: ['SOX', 'PCI DSS'],
                benefits: ['Asset lifecycle tracking', 'Change approval workflows', 'Audit documentation'],
              },
              {
                icon: Heart,
                title: 'Healthcare',
                frameworks: ['HIPAA', 'HITECH'],
                benefits: ['Medical device inventory', 'PHI system tracking', 'Security configurations'],
              },
              {
                icon: Lock,
                title: 'Government',
                frameworks: ['FedRAMP', 'FISMA'],
                benefits: ['Authorization boundaries', 'Continuous monitoring', 'Security controls'],
              },
              {
                icon: Cloud,
                title: 'Technology',
                frameworks: ['SOC 2', 'ISO 27001'],
                benefits: ['Cloud resource tracking', 'Software licensing', 'Development assets'],
              },
            ].map((industry, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 group">
                  <CardHeader className="pb-4">
                    <div
                      style={{ backgroundColor: `${BOSSS_BLUE}15` }}
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    >
                      <industry.icon className="w-6 h-6" style={{ color: BOSSS_BLUE }} />
                    </div>

                    <CardTitle className="text-lg group-hover:text-blue-600 transition-colors">
                      {industry.title}
                    </CardTitle>

                    <div className="flex flex-wrap gap-1 mt-2">
                      {industry.frameworks.map((fw, i) => (
                        <span key={i} className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                          {fw}
                        </span>
                      ))}
                    </div>
                  </CardHeader>

                  <CardContent>
                    <ul className="space-y-2">
                      {industry.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start text-sm text-gray-600">
                          <CheckCircle style={{ color: BOSSS_BLUE }} className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Agent Banner */}
      <AIAgentBanner
        toolsCount={15}
        accentColor="#3b82f6"
        agentName="CMDB Agent"
        description="Your AI-powered asset management copilot. Ask about devices, software inventory, or configuration changes—get instant answers."
        exampleQueries={[
          '"Show me all assets missing security agents"',
          '"What servers are running outdated OS?"',
          '"List all applications in production"',
          '"Which devices are offline?"',
        ]}
      />

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: BOSSS_BLUE }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Take Control of Your Assets?
            </h2>

            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              See how BOSSS CMDB can give you complete visibility into your infrastructure
              with automated discovery and real-time synchronization.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  className="bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                  onClick={() => onNavigateToTop('/demo')}
                >
                  Schedule Your Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>

              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  variant="outline"
                  className="border-2 border-white/80 text-white bg-white/10 hover:bg-white/20 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold backdrop-blur-sm"
                  onClick={() => onNavigateToTop('/contact')}
                >
                  Contact Sales
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
